"use client";

import { useMemo, useState } from "react";
import { analyzeReadability, readingEaseBand } from "@/lib/readability";
import { useI18n } from "@/components/providers/I18nProvider";
import { uiIntlLocale } from "@/lib/i18n/languages";

/**
 * Deliberately mostly clean prose with one bureaucratic outlier, because that
 * is the real use case: the score is only useful relatively, and the longest-
 * sentence list should visibly point at the culprit.
 */
const SAMPLE = `The rain had been falling since Tuesday. Nel counted the drips into the bucket by the door — eleven a minute, down from twenty — and decided the roof would hold until spring.

She had written to the landlord twice. The reply explained that any comprehensive remediation of the aforementioned structural deterioration would necessitate a financial commitment disproportionate to the anticipated residual value of the property.

She read it twice. Then she went out and bought a second bucket.`;

/** One number and its label. */
function Score({
  value,
  label,
  hint,
}: {
  value: string;
  label: string;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      <div className="mt-0.5 text-sm font-medium">{label}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

/**
 * Readability scores for pasted prose, computed in the browser.
 *
 * Shares its formulas with the in-app readability metric, so the number here
 * is the number a writer sees inside Novilot.
 */
export function ReadabilityChecker() {
  const { locale, t } = useI18n();
  const [text, setText] = useState("");
  const r = useMemo(() => analyzeReadability(text), [text]);
  const band = readingEaseBand(r.readingEase);
  const numberLocale = uiIntlLocale(locale);
  const formatNumber = (value: number) => value.toLocaleString(numberLocale);
  const formatDecimal = (value: number) => value.toLocaleString(numberLocale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const bandLabel = t(
    band.label === "Very easy"
      ? "marketing.readability.veryEasy"
      : band.label === "Easy"
        ? "marketing.readability.easy"
        : band.label === "Fairly easy"
          ? "marketing.readability.fairlyEasy"
          : band.label === "Plain English"
            ? "marketing.readability.plainEnglish"
            : band.label === "Fairly difficult"
              ? "marketing.readability.fairlyDifficult"
              : band.label === "Difficult"
                ? "marketing.readability.difficult"
                : "marketing.readability.veryDifficult",
  );
  const sentenceLabel = locale === "ar"
    ? r.sentences === 1
      ? "جملة"
      : r.sentences === 2
        ? "جملتان"
        : "جمل"
    : r.sentences === 1
      ? t("marketing.readability.sentence")
      : t("marketing.readability.sentences");

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label htmlFor="rd-input" className="text-sm font-medium">
          {t("marketing.checker.pasteWriting")}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setText(SAMPLE)}
            className="min-h-[36px] rounded-lg border border-border px-3 text-sm hover:bg-muted"
          >
            {t("marketing.checker.trySample")}
          </button>
          <button
            type="button"
            onClick={() => setText("")}
            disabled={!text}
            className="min-h-[36px] rounded-lg border border-border px-3 text-sm hover:bg-muted disabled:opacity-40"
          >
            {t("marketing.checker.clear")}
          </button>
        </div>
      </div>

      <textarea
        id="rd-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={10}
        spellCheck={false}
        placeholder={t("marketing.checker.placeholder")}
        className="mt-2 w-full resize-y rounded-xl border border-border bg-background p-4 text-sm leading-relaxed"
      />

      {r.sentences > 0 && (
        <div data-testid="rd-results">
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Score
              value={formatDecimal(r.readingEase)}
              label={t("marketing.readability.readingEase")}
              hint={bandLabel}
            />
            <Score
              value={r.gradeLevel < 1 ? "<1" : formatDecimal(r.gradeLevel)}
              label={t("marketing.readability.gradeLevel")}
              hint={t("marketing.readability.schoolYear")}
            />
            <Score
              value={formatDecimal(r.avgSentenceLength)}
              label={t("marketing.readability.averageSentence")}
              hint={t("marketing.readability.words")}
            />
            <Score
              value={formatDecimal(r.gunningFog)}
              label={t("marketing.readability.gunningFog")}
              hint={t("marketing.readability.longWords", { count: formatNumber(r.complexWords) })}
            />
          </div>

          <p
            data-testid="rd-summary"
            className="mt-3 text-sm text-muted-foreground"
          >
            {t(band.easy ? "marketing.readability.summaryEasy" : "marketing.readability.summaryDense", {
              words: formatNumber(r.words),
              sentences: formatNumber(r.sentences),
              sentenceLabel,
              band: bandLabel,
            })}
          </p>

          {r.longestSentences.length > 0 && (
            <>
              <h3 className="mt-8 text-lg font-semibold">{t("marketing.readability.longestSentences")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("marketing.readability.sentenceGuidance")}
              </p>
              <ol
                data-testid="rd-longest"
                className="mt-3 space-y-3 text-sm leading-relaxed"
              >
                {r.longestSentences.map((s, i) => (
                  <li key={i} className="rounded-xl border border-border p-3">
                    <span className="mr-2 rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums">
                      {formatNumber(s.words)} {t("marketing.readability.words")}
                    </span>{" "}
                    <span className="text-muted-foreground">
                      {s.text.length > 300 ? s.text.slice(0, 300) + "…" : s.text}
                    </span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}
